import { useLazyGetSensitiveDataFieldValueQuery } from "apis/sensitiveData";
import { useEffect, useMemo, useState } from "react";
import { SensitiveDataFormField } from "types/Models/sensitiveData";

const EMPTY_SECRETS: Record<string, string> = {};

export const useSensitiveDataSecretValues = ({
  entityId,
  formId,
  fields,
  skip,
}: {
  entityId?: string;
  formId: string;
  fields: SensitiveDataFormField[];
  skip: boolean;
}): { secretByFieldKey: Record<string, string>; isLoading: boolean } => {
  const [getFieldValue] = useLazyGetSensitiveDataFieldValueQuery();
  const [secretByFieldKey, setSecretByFieldKey] = useState(EMPTY_SECRETS);
  const [isLoading, setIsLoading] = useState(false);

  const encryptedFields = useMemo(
    () =>
      fields
        .filter(({ is_encrypted }) => is_encrypted)
        .map(({ uuid, field_key }) => ({ uuid, field_key })),
    [fields]
  );

  useEffect(() => {
    if (skip || !entityId || !formId || encryptedFields.length === 0) return;

    let isCurrent = true;
    setIsLoading(true);

    const loadSecrets = async () => {
      const results = await Promise.allSettled(
        encryptedFields.map(async ({ uuid, field_key }) => {
          const { encoded_value } = await getFieldValue({
            entityId,
            formId,
            attributeId: uuid,
          }).unwrap();

          return [field_key, encoded_value] as const;
        })
      );

      if (!isCurrent) return;

      setSecretByFieldKey(
        Object.fromEntries(
          results.flatMap((result) =>
            result.status === "fulfilled" ? [result.value] : []
          )
        )
      );
      setIsLoading(false);
    };

    void loadSecrets();

    return () => {
      isCurrent = false;
    };
  }, [encryptedFields, entityId, formId, skip, getFieldValue]);

  return { secretByFieldKey, isLoading };
};
